import LazyWrap from '@/components/LazyWrap';
import ScrollView from '@/components/ScrollView';
import Layout from '@/components/ux/Layout';
import { capturePosthogEvent, log } from '@/helpers';
import { useQuery } from '@/hooks';
import { useMaximizeStore } from '@/stores';
import { useCampaignReportCampaignStore } from '@/stores/campaign-report/useCampaignReportCampaignStore';
import { getCampaignReportFiltersStore } from '@/stores/campaign-reports/createCampaignReportFiltersStore.ts';
import { getCampaignReportTopConvertingStore } from '@/stores/campaign-reports/createCampaignReportTopConvertingStore';
import { Flex, SegmentedButton, Typography, useToast } from '@alpine-iq/ui-library';
import React from 'react';

const CampaignInformationReportCard = React.lazy(() => import('./components/CampaignInformationReportCard'));
const CampaignReportMoreButton = React.lazy(() => import('./components/CampaignReportMoreButton'));
const ReportingConversionOverviewCard = React.lazy(() => import('../campaign-reports/components/ReportingConversionOverviewCard'));
const ReportingTopConvertedCard = React.lazy(() => import('../campaign-reports/components/ReportingTopConvertedCard'));
const ReportingConversionsBreakdownCard = React.lazy(() => import('../campaign-reports/components/ReportingConversionsBreakdownCard'));
const ReportingChannelPerformanceCard = React.lazy(() => import('../campaign-reports/components/ReportingChannelPerformanceCard'));
const EngagementPerformedBeforeConvertingCard = React.lazy(() => import('../campaign-reports/components/EngagementPerformedBeforeConvertingCard'));
const CampaignLinkTrackingBreakoutReportCard = React.lazy(() => import('./components/CampaignLinkTrackingBreakoutReportCard'));

export default function CampaignReportsPage() {
	const query = useQuery();
	const campaignID = query.get('campaignID') ?? undefined;
	const templateID = query.get('templateID') ?? undefined;
	const retailerCampaignID = query.get('retailerCampaignID') ?? undefined;
	const templateIDProp = templateID ? Number(templateID) : undefined;

	const { addToast } = useToast();
	const { isMaximized, unmaximize } = useMaximizeStore();

	const { campaign, fetchCampaign } = useCampaignReportCampaignStore();

	// Get global filters for all report page
	const filtersStore = getCampaignReportFiltersStore(campaignID, templateIDProp, retailerCampaignID)((s) => s);
	const { rawFilters, setRawFilters, initialize, getReportFiltersFromRawFilters } = filtersStore;

	React.useEffect(() => {
		if (!campaignID) {
			addToast({
				message: 'No campaignID provided',
				type: 'danger',
				duration: 10000,
			});
			return;
		}
		if (campaign?.id !== campaignID) {
			fetchCampaign(campaignID).then(() => {
				initialize();
			});
			return;
		}

		log('initialize', campaignID, templateID, retailerCampaignID);

		initialize();
	}, [campaignID]);

	const variantIDs = React.useMemo(() => {
		return campaign?.abVariants?.map((variant) => String(variant.id)) ?? [];
	}, [campaign]);

	const isCoMarketingReport = !!templateIDProp || !!retailerCampaignID;

	if (!campaignID) {
		if (isMaximized) {
			unmaximize();
		}
		return <></>;
	}

	return (
		<>
			<Layout>
				<ScrollView height="calc(100vh - var(--app-header-height))">
					<Flex
						column
						gap="24"
						padding="24px 40px"
					>
						<Flex
							wrap
							align="center"
							justify="space-between"
						>
							<Typography variant="h1">{campaign?.name ?? 'Unnamed Campaign'}</Typography>
							<Flex align="center">
								<SegmentedButton<'true' | 'false'>
									options={[
										{ label: 'Strict Attribution', value: 'true' },
										{ label: 'Broad Attribution', value: 'false' },
									]}
									value={rawFilters.strictAttribution}
									onClick={(strictAttribution) => {
										capturePosthogEvent('Campaign Report - Change Attribution Method', {
											strictAttribution,
										});
										setRawFilters({ ...rawFilters, strictAttribution });
									}}
								/>
								{!isCoMarketingReport && (
									<LazyWrap fallback={<></>}>
										<CampaignReportMoreButton
											campaignId={campaignID}
											templateID={templateIDProp}
											retailerCampaignID={retailerCampaignID}
										/>
									</LazyWrap>
								)}
							</Flex>
						</Flex>

						<LazyWrap>
							<CampaignInformationReportCard />
							<ReportingConversionOverviewCard
								campaignID={campaignID}
								variantIDs={variantIDs}
								conversionMethod={rawFilters.strictAttribution ? 'strict' : 'broad'}
								retailerCampaignID={retailerCampaignID}
								templateID={templateIDProp}
							/>
							<ReportingTopConvertedCard
								campaignID={campaignID}
								variantIDs={variantIDs}
								retailerCampaignID={retailerCampaignID}
								onChange={(type) => {
									capturePosthogEvent(isCoMarketingReport ? 'Co-Marketing Campaign Report - Change Top Converting' : 'Campaign Report - Change Top Converting', {
										type,
									});
									if (!variantIDs.length) {
										const filters = getReportFiltersFromRawFilters(campaignID);
										getCampaignReportTopConvertingStore(campaignID, undefined, retailerCampaignID).getState().reset(filters, type);
										return;
									}

									for (const variantID of variantIDs) {
										const filters = getReportFiltersFromRawFilters(campaignID, variantID);
										getCampaignReportTopConvertingStore(campaignID, variantID, retailerCampaignID).getState().reset(filters, type);
									}
								}}
							/>
							<ReportingConversionsBreakdownCard
								campaignID={campaignID}
								variantIDs={variantIDs}
								retailerCampaignID={retailerCampaignID}
								templateID={templateIDProp}
							/>
							<ReportingChannelPerformanceCard
								campaignID={campaignID}
								variantIDs={variantIDs}
								templateID={templateIDProp}
								retailerCampaignID={retailerCampaignID}
							/>
							<EngagementPerformedBeforeConvertingCard
								campaignID={campaignID}
								variantIDs={variantIDs}
								retailerCampaignID={retailerCampaignID}
							/>
							<CampaignLinkTrackingBreakoutReportCard
								campaignID={campaignID}
								variantIDs={variantIDs}
								retailerCampaignID={retailerCampaignID}
							/>
						</LazyWrap>
					</Flex>
				</ScrollView>
			</Layout>
		</>
	);
}

export const onSelectCampaignReport = (campaignID: string) => {
	log('on select campaign report', campaignID);
	if (!campaignID) return;

	const url = new URL(window.location.href);
	url.searchParams.set('campaignID', campaignID.toString());
	window.history.pushState({}, '', url.toString());

	const maxStore = useMaximizeStore.getState();
	const maximizedKey = 'campaign-report';
	maxStore.maximize(() => <CampaignReportsPage />, {
		maximizedKey,
	});
	maxStore.addUnmaximizeCallback(
		() => {
			useCampaignReportCampaignStore.getState().reset();
		},
		{ maximizedKey },
	);
	maxStore.trackParam('campaignID', maximizedKey);

	capturePosthogEvent('Campaign Reports - Campaign Report Selected', {
		campaignID: campaignID,
	});
};

export const onSelectCoMarketingCampaignReport = (campaignID: string, templateID: number, retailerCampaignID: string) => {
	log('on select co-marketing campaign report', campaignID, templateID, retailerCampaignID);
	if (!campaignID || !templateID || !retailerCampaignID) return;

	const url = new URL(window.location.href);
	url.searchParams.set('campaignID', campaignID.toString());
	url.searchParams.set('templateID', templateID.toString());
	url.searchParams.set('retailerCampaignID', retailerCampaignID);
	window.history.pushState({}, '', url.toString());

	const maxStore = useMaximizeStore.getState();
	const maximizedKey = 'campaign-report-retailer';
	maxStore.maximize(() => <CampaignReportsPage />, {
		maximizedKey,
		centerTitle: 'Sponsored Retailer Report',
	});
	useMaximizeStore.getState().addUnmaximizeCallback(
		() => {
			useCampaignReportCampaignStore.getState().reset();
		},
		{ maximizedKey },
	);

	maxStore.trackParam('retailerCampaignID', maximizedKey);

	capturePosthogEvent('Campaign Reports - Co-Marketing Campaign Report Selected', {
		campaignID: campaignID,
		templateID: templateID,
		retailerCampaignID: retailerCampaignID,
	});
};
